import React, { useState, useEffect, useCallback } from 'react';
import { T, FONTS, SHADOW } from '../../constants/theme';
import { fetchReservation, checkIn, checkOut, cancelReservation, markNoShow, confirmReservation, fetchFolio, fetchRooms, previewRoomChange, changeRoom, fetchReservationGuests, addReservationGuest, removeReservationGuest } from '../../services/api.js';
import { useOfflineInvalidation, Modal } from '../../components/ui.jsx';
import InvoicePrint from '../../components/folio/InvoicePrint.jsx';
import GuestRegistrationPrint from '../../components/folio/GuestRegistrationPrint.jsx';
import PaymentRequestPrint from '../../components/folio/PaymentRequestPrint.jsx';
import CheckInGateAlert from '../../components/corporate/CheckInGateAlert.jsx';
// warmCacheForView is a no-op on desktop (SQLite kept warm by SyncWorker)
const warmCacheForView = () => {};

/* ── Status config ─────────────────────────────────────────────────────────── */
const STATUS = {
  inquiry:     { color:'#64748B', bg:'#F1F5F9', label:'Inquiry',     icon:'❓' },
  tentative:   { color:'#D97706', bg:'#FFFBEB', label:'Tentative',   icon:'⏳' },
  confirmed:   { color:'#1A7F8E', bg:'#E6F4F6', label:'Confirmed',   icon:'✅' },
  checked_in:  { color:'#1A7A4A', bg:'#EAF7EE', label:'Checked In',  icon:'🛎️' },
  checked_out: { color:'#4A5568', bg:'#F1F5F9', label:'Checked Out', icon:'🚪' },
  cancelled:   { color:'#C0392B', bg:'#FDECEA', label:'Cancelled',   icon:'✕'  },
  no_show:     { color:'#C05621', bg:'#FFF8F5', label:'No Show',     icon:'⛔' },
};
const STATUS_ORDER = ['inquiry','tentative','confirmed','checked_in','checked_out'];

/* ── helpers ───────────────────────────────────────────────────────────────── */
const fmtDate = d => {
  if (!d) return '—';
  return new Date(d + 'T00:00:00').toLocaleDateString('en-GB',{weekday:'short',day:'numeric',month:'short',year:'numeric'});
};
const fmtDT = s => {
  if (!s) return '—';
  return new Date(s).toLocaleString('en-GB',{day:'numeric',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'});
};
const cap = s => s ? s.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase()) : '—';

/* ── micro components ──────────────────────────────────────────────────────── */
function Chip({ label, color, bg }) {
  return (
    <span style={{ display:'inline-flex',alignItems:'center',gap:4,padding:'3px 10px',
      borderRadius:99,fontSize:10,fontWeight:700,letterSpacing:.6,textTransform:'uppercase',
      color,background:bg,border:`1px solid ${color}30` }}>{label}</span>
  );
}

function InfoRow({ label, value, accent }) {
  return (
    <div style={{ display:'flex',justifyContent:'space-between',alignItems:'flex-start',
      padding:'9px 0',borderBottom:`1px solid ${T.border}` }}>
      <span style={{ fontSize:11,color:T.muted,fontWeight:600,flexShrink:0,paddingRight:8 }}>{label}</span>
      <span style={{ fontSize:12,color:accent||T.navy,fontWeight:600,textAlign:'right',maxWidth:'65%',lineHeight:1.4 }}>
        {value||'—'}
      </span>
    </div>
  );
}

function SectionCard({ title, icon, children, noPad, accent }) {
  return (
    <div style={{ background:T.white,border:`1px solid ${accent?accent+'30':T.border}`,
      borderRadius:14,overflow:'hidden',boxShadow:SHADOW.sm }}>
      <div style={{ padding:'13px 18px',borderBottom:`1px solid ${accent?accent+'20':T.border}`,
        background:accent?`${accent}06`:T.surface,display:'flex',alignItems:'center',gap:9 }}>
        <span style={{ fontSize:16 }}>{icon}</span>
        <p style={{ fontSize:13,fontWeight:700,color:T.navy }}>{title}</p>
      </div>
      <div style={noPad?{}:{ padding:'4px 18px 14px' }}>{children}</div>
    </div>
  );
}

/* ── Additional people included under this SAME reservation/room ───────────── */
/* Feature: Multiple people under one individual reservation. The focal/
 * primary guest stays exactly as shown in the "Guest Profile" card above —
 * this section only manages the additional people recorded alongside them.
 * Adding, editing or removing someone here never touches the folio, room
 * assignment or reservation totals. */
function AdditionalGuestsSection({ reservationId, focalName }) {
  const [people,  setPeople]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding,  setAdding]  = useState(false);
  const [form,    setForm]    = useState({ fullName: '', nationality: '', idType: '', idNumber: '', phone: '' });
  const [err,     setErr]     = useState('');
  const [saving,  setSaving]  = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    fetchReservationGuests(reservationId)
      .then(d => setPeople(Array.isArray(d) ? d : []))
      .catch(() => setPeople([]))
      .finally(() => setLoading(false));
  }, [reservationId]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!form.fullName.trim()) { setErr('Full name is required'); return; }
    setSaving(true); setErr('');
    try {
      await addReservationGuest(reservationId, form);
      setForm({ fullName: '', nationality: '', idType: '', idNumber: '', phone: '' });
      setAdding(false);
      load();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const handleRemove = async (guestRowId) => {
    try { await removeReservationGuest(reservationId, guestRowId); load(); }
    catch (e) { setErr(e.message); }
  };

  return (
    <SectionCard title="Additional People Included" icon="👥">
      <p style={{ fontSize:11, color:T.muted, padding:'2px 0 10px', lineHeight:1.5 }}>
        These people share room and reservation <strong>{focalName ? `with ${focalName}` : 'with the primary guest'}</strong> — they are not separate reservations.
      </p>

      {loading ? (
        <p style={{ fontSize:12, color:T.muted, padding:'6px 0' }}>Loading…</p>
      ) : people.length === 0 && !adding ? (
        <p style={{ fontSize:12, color:T.muted, padding:'6px 0 10px' }}>No additional people recorded yet.</p>
      ) : (
        people.map(p => (
          <div key={p.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
            padding:'9px 0', borderBottom:`1px solid ${T.border}` }}>
            <div>
              <p style={{ fontSize:12, fontWeight:700, color:T.navy }}>{p.fullName}</p>
              <p style={{ fontSize:10, color:T.muted, marginTop:2 }}>
                {[p.nationality, p.idType && p.idNumber ? `${cap(p.idType)}: ${p.idNumber}` : null, p.phone]
                  .filter(Boolean).join(' • ') || '—'}
              </p>
            </div>
            <button onClick={() => handleRemove(p.id)} style={{
              background:'transparent', border:'none', color:T.danger, fontSize:11,
              fontWeight:700, cursor:'pointer', padding:'4px 8px',
            }}>Remove</button>
          </div>
        ))
      )}

      {err && <p style={{ fontSize:11, color:T.danger, padding:'8px 0 0' }}>{err}</p>}

      {adding ? (
        <div style={{ marginTop:10, display:'flex', flexDirection:'column', gap:8 }}>
          <input placeholder="Full name *" value={form.fullName}
            onChange={e => setForm(f => ({ ...f, fullName: e.target.value }))}
            style={{ border:`1px solid ${T.border}`, borderRadius:8, padding:'8px 10px', fontSize:12 }} />
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
            <input placeholder="Nationality" value={form.nationality}
              onChange={e => setForm(f => ({ ...f, nationality: e.target.value }))}
              style={{ border:`1px solid ${T.border}`, borderRadius:8, padding:'8px 10px', fontSize:12 }} />
            <input placeholder="Phone" value={form.phone}
              onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
              style={{ border:`1px solid ${T.border}`, borderRadius:8, padding:'8px 10px', fontSize:12 }} />
            <input placeholder="ID type (passport, national_id…)" value={form.idType}
              onChange={e => setForm(f => ({ ...f, idType: e.target.value }))}
              style={{ border:`1px solid ${T.border}`, borderRadius:8, padding:'8px 10px', fontSize:12 }} />
            <input placeholder="ID number" value={form.idNumber}
              onChange={e => setForm(f => ({ ...f, idNumber: e.target.value }))}
              style={{ border:`1px solid ${T.border}`, borderRadius:8, padding:'8px 10px', fontSize:12 }} />
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <button disabled={saving} onClick={handleAdd} style={{
              flex:1, background:T.teal, color:'#fff', border:'none', borderRadius:8,
              padding:'8px 0', fontSize:12, fontWeight:700, cursor:'pointer',
            }}>{saving ? 'Saving…' : 'Add Person'}</button>
            <button onClick={() => { setAdding(false); setErr(''); }} style={{
              background:T.surface, border:`1px solid ${T.border}`, borderRadius:8,
              padding:'8px 14px', fontSize:12, fontWeight:700, color:T.slate, cursor:'pointer',
            }}>Cancel</button>
          </div>
        </div>
      ) : (
        <button onClick={() => setAdding(true)} style={{
          marginTop:10, background:'transparent', border:`1.5px dashed ${T.teal}`, borderRadius:8,
          padding:'8px 0', fontSize:12, fontWeight:700, color:T.teal, cursor:'pointer', width:'100%',
        }}>+ Add Person</button>
      )}
    </SectionCard>
  );
}

/* ── status journey bar ────────────────────────────────────────────────────── */
function StatusBar({ status }) {
  const active = STATUS_ORDER.indexOf(status);
  const isTerminal = ['cancelled','no_show'].includes(status);
  const cfg = STATUS[status] || {};
  return (
    <div style={{ background:T.white,border:`1px solid ${T.border}`,borderRadius:14,
      padding:'14px 20px',boxShadow:SHADOW.sm }}>
      {isTerminal ? (
        <div style={{ display:'flex',alignItems:'center',gap:12 }}>
          <span style={{ fontSize:22 }}>{cfg.icon}</span>
          <div>
            <p style={{ fontSize:13,fontWeight:700,color:cfg.color }}>{cfg.label}</p>
            <p style={{ fontSize:11,color:T.muted,marginTop:1 }}>
              {status==='cancelled'?'This reservation has been cancelled.':'Guest did not arrive for this reservation.'}
            </p>
          </div>
        </div>
      ) : (
        <div style={{ display:'flex',alignItems:'center',gap:0 }}>
          {STATUS_ORDER.map((s,i) => {
            const c    = STATUS[s];
            const done = i < active;
            const curr = i === active;
            return (
              <React.Fragment key={s}>
                <div style={{ display:'flex',flexDirection:'column',alignItems:'center',gap:4,minWidth:0,flex:1 }}>
                  <div style={{ width:30,height:30,borderRadius:'50%',display:'flex',
                    alignItems:'center',justifyContent:'center',fontSize:13,transition:'all .2s',
                    background:done||curr?c.color:T.border,
                    boxShadow:curr?`0 0 0 4px ${c.color}25`:'none',
                    color:done||curr?'#fff':T.muted }}>
                    {done?'✓':c.icon}
                  </div>
                  <p style={{ fontSize:9,fontWeight:curr?800:600,
                    color:curr?c.color:done?T.muted:T.border,textAlign:'center',lineHeight:1.2,
                    letterSpacing:.3,textTransform:'uppercase' }}>
                    {c.label}
                  </p>
                </div>
                {i < STATUS_ORDER.length-1 && (
                  <div style={{ height:2,flex:0.6,background:done?T.teal:T.border,
                    marginBottom:20,transition:'all .3s',flexShrink:0 }} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ── action button ─────────────────────────────────────────────────────────── */
function Btn({ children, onClick, disabled, variant='default', icon }) {
  const styles = {
    default: { bg:T.surface,  border:T.border,   text:T.slate  },
    primary: { bg:T.teal,     border:T.teal,     text:'#fff'   },
    success: { bg:T.success,  border:T.success,  text:'#fff'   },
    navy:    { bg:T.navy,     border:T.navy,     text:'#fff'   },
    gold:    { bg:T.gold,     border:T.gold,     text:T.navy   },
    danger:  { bg:'transparent', border:T.danger,  text:T.danger },
    warning: { bg:'transparent', border:'#C05621', text:'#C05621'},
  };
  const s = styles[variant] || styles.default;
  return (
    <button onClick={onClick} disabled={disabled} style={{
      display:'inline-flex',alignItems:'center',gap:6,padding:'10px 18px',
      borderRadius:10,border:`1.5px solid ${s.border}`,background:s.bg,
      color:s.text,fontSize:12,fontWeight:700,cursor:disabled?'not-allowed':'pointer',
      fontFamily:FONTS.body,opacity:disabled?.6:1,transition:'all .15s',whiteSpace:'nowrap',
    }}>
      {icon && <span style={{ fontSize:15 }}>{icon}</span>}
      {children}
    </button>
  );
}

/* ── no-show modal ─────────────────────────────────────────────────────────── */
function NoShowModal({ open, res, property, onClose, onConfirm, busy }) {
  const currency     = res?.currency || property?.currency || 'RWF';
  const ratePerNight = parseFloat(res?.ratePerNight || 0);
  const totalAmount  = parseFloat(res?.totalAmount  || 0);
  const policy       = property?.noShowPolicy || 'NONE';

  const suggestedFee = (() => {
    if (policy === 'FIRST_NIGHT')  return ratePerNight;
    if (policy === 'FULL_BOOKING') return totalAmount;
    if (policy === 'PERCENTAGE')   return Math.round(totalAmount * parseFloat(property?.noShowFeePercentage||0) / 100 * 100) / 100;
    if (policy === 'FIXED_AMOUNT') return parseFloat(property?.noShowFeeAmount || 0);
    return 0;
  })();

  const [feeMode,   setFeeMode]   = useState(policy !== 'NONE' ? 'policy' : 'none');
  const [customFee, setCustomFee] = useState('');
  const [reason,    setReason]    = useState('Guest did not arrive');

  const effectiveFee = feeMode === 'none' ? 0
    : feeMode === 'custom' ? parseFloat(customFee || 0)
    : suggestedFee;

  if (!open || !res) return null;

  const POLICY_LABEL = {
    NONE:'No Fee Policy',
    FIRST_NIGHT:`First Night — ${currency} ${ratePerNight.toLocaleString()}`,
    PERCENTAGE:`${property?.noShowFeePercentage||0}% of Total — ${currency} ${suggestedFee.toLocaleString()}`,
    FIXED_AMOUNT:`Fixed Fee — ${currency} ${parseFloat(property?.noShowFeeAmount||0).toLocaleString()}`,
    FULL_BOOKING:`Full Booking — ${currency} ${totalAmount.toLocaleString()}`,
  };

  return (
    <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,.6)',zIndex:1200,
      display:'flex',alignItems:'center',justifyContent:'center',padding:16 }}>
      <div style={{ background:'#fff',borderRadius:20,width:'100%',maxWidth:500,
        boxShadow:'0 32px 80px rgba(0,0,0,.25)',overflow:'hidden' }}>
        <div style={{ background:'linear-gradient(135deg,#7B341E,#C05621)',padding:'18px 22px',
          display:'flex',alignItems:'center',gap:12 }}>
          <div style={{ width:40,height:40,borderRadius:10,background:'rgba(255,255,255,.2)',
            display:'flex',alignItems:'center',justifyContent:'center',fontSize:20 }}>⛔</div>
          <div>
            <p style={{ color:'#fff',fontSize:15,fontWeight:800 }}>Mark as No-Show</p>
            <p style={{ color:'rgba(255,255,255,.75)',fontSize:11,marginTop:2 }}>
              {res.guestName} · {res.confirmationNumber}
            </p>
          </div>
          <button onClick={onClose} style={{ marginLeft:'auto',background:'rgba(255,255,255,.2)',
            border:'none',borderRadius:8,width:28,height:28,color:'#fff',cursor:'pointer',
            fontSize:16,display:'flex',alignItems:'center',justifyContent:'center' }}>✕</button>
        </div>

        <div style={{ padding:'18px 22px' }}>
          <div style={{ background:'#FFF8F5',borderRadius:10,padding:'10px 14px',
            border:'1px solid #FECBA1',marginBottom:16 }}>
            <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:6 }}>
              {[['Arrival',res.arrivalDate],['Room Type',res.roomTypeName||'—'],
                ['Rate/Night',`${currency} ${ratePerNight.toLocaleString()}`],
                ['Total',`${currency} ${totalAmount.toLocaleString()}`],
              ].map(([l,v])=>(
                <div key={l}>
                  <p style={{ fontSize:9,color:'#92400E',fontWeight:700,textTransform:'uppercase',marginBottom:1 }}>{l}</p>
                  <p style={{ fontSize:11,color:'#7C2D12',fontWeight:600 }}>{v}</p>
                </div>
              ))}
            </div>
          </div>

          <p style={{ fontSize:11,fontWeight:700,color:T.navy,marginBottom:8,letterSpacing:.3,textTransform:'uppercase' }}>No-Show Fee</p>
          <div style={{ display:'flex',flexDirection:'column',gap:6,marginBottom:14 }}>
            {policy !== 'NONE' && (
              <label style={{ display:'flex',alignItems:'center',gap:10,padding:'10px 13px',
                borderRadius:9,border:`2px solid ${feeMode==='policy'?'#C05621':T.border}`,
                background:feeMode==='policy'?'#FFF8F5':T.white,cursor:'pointer' }}>
                <input type="radio" checked={feeMode==='policy'} onChange={()=>setFeeMode('policy')} />
                <div>
                  <p style={{ fontSize:12,fontWeight:700,color:'#7C2D12' }}>Property Policy — {POLICY_LABEL[policy]}</p>
                  <p style={{ fontSize:10,color:T.muted }}>As configured in Property Settings</p>
                </div>
              </label>
            )}
            <label style={{ display:'flex',alignItems:'center',gap:10,padding:'10px 13px',
              borderRadius:9,border:`2px solid ${feeMode==='custom'?T.teal:T.border}`,
              background:feeMode==='custom'?`${T.teal}06`:T.white,cursor:'pointer' }}>
              <input type="radio" checked={feeMode==='custom'} onChange={()=>setFeeMode('custom')} />
              <div style={{ flex:1 }}>
                <p style={{ fontSize:12,fontWeight:700,color:T.navy }}>Custom Amount</p>
                {feeMode==='custom' && (
                  <input type="number" min="0" step="0.01" value={customFee}
                    onChange={e=>setCustomFee(e.target.value)}
                    placeholder={`Amount in ${currency}`}
                    style={{ marginTop:5,width:'100%',border:`1px solid ${T.border}`,borderRadius:7,
                      padding:'7px 10px',fontSize:12,fontFamily:FONTS.body,outline:'none',color:T.navy }} />
                )}
              </div>
            </label>
            <label style={{ display:'flex',alignItems:'center',gap:10,padding:'10px 13px',
              borderRadius:9,border:`2px solid ${feeMode==='none'?T.muted:T.border}`,
              background:T.white,cursor:'pointer' }}>
              <input type="radio" checked={feeMode==='none'} onChange={()=>setFeeMode('none')} />
              <div>
                <p style={{ fontSize:12,fontWeight:700,color:T.navy }}>No Charge</p>
                <p style={{ fontSize:10,color:T.muted }}>Mark no-show without any fee</p>
              </div>
            </label>
          </div>

          {effectiveFee > 0 && (
            <div style={{ display:'flex',alignItems:'center',gap:10,padding:'10px 14px',
              background:'#FEF3C7',borderRadius:9,marginBottom:14,border:'1px solid #FDE68A' }}>
              <span style={{ fontSize:16 }}>💳</span>
              <p style={{ fontSize:11,color:'#78350F',fontWeight:600 }}>
                <strong>{currency} {effectiveFee.toLocaleString()}</strong> will be posted to folio and guest notified by email.
              </p>
            </div>
          )}

          <div style={{ marginBottom:16 }}>
            <p style={{ fontSize:11,fontWeight:700,color:T.navy,marginBottom:5 }}>Reason</p>
            <textarea value={reason} onChange={e=>setReason(e.target.value)} rows={2}
              style={{ width:'100%',border:`1px solid ${T.border}`,borderRadius:8,
                padding:'8px 11px',fontSize:12,fontFamily:FONTS.body,resize:'none',
                color:T.navy,outline:'none',boxSizing:'border-box' }} />
          </div>

          <div style={{ display:'flex',gap:10 }}>
            <button onClick={onClose} style={{ flex:1,padding:'11px',borderRadius:9,
              background:T.surface,border:`1px solid ${T.border}`,fontSize:12,fontWeight:600,
              cursor:'pointer',color:T.muted,fontFamily:FONTS.body }}>Cancel</button>
            <button disabled={busy} onClick={()=>onConfirm(effectiveFee>0?effectiveFee:null,reason)}
              style={{ flex:2,padding:'11px',borderRadius:9,
                background:'linear-gradient(135deg,#7B341E,#C05621)',border:'none',
                fontSize:12,fontWeight:700,cursor:busy?'not-allowed':'pointer',
                color:'#fff',fontFamily:FONTS.body,opacity:busy?.7:1 }}>
              {busy?'Processing…':effectiveFee>0
                ?`⛔ Confirm No-Show · Charge ${currency} ${effectiveFee.toLocaleString()}`
                :'⛔ Confirm No-Show (No Charge)'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── MAIN ──────────────────────────────────────────────────────────────────── */
export default function ReservationDetail({ reservationId, property, onBack, onNavigate }) {
  const [res,        setRes]        = useState(null);
  const [loadErr,    setLoadErr]    = useState('');
  const [loading,    setLoading]    = useState(true);
  const [toast,      setToast]      = useState('');
  const [toastType,  setToastType]  = useState('');
  const [busy,       setBusy]       = useState(false);
  const [noShowOpen, setNoShowOpen] = useState(false);
  const [cancelConf, setCancelConf] = useState(false);
  const [showInvoice,    setShowInvoice]    = useState(false);
  const [invoiceFolio,   setInvoiceFolio]   = useState(null);
  const [showGuestReg,   setShowGuestReg]   = useState(false);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [showPaymentRequest,    setShowPaymentRequest]    = useState(false);
  const [paymentRequestFolio,   setPaymentRequestFolio]   = useState(null);
  const [paymentRequestLoading, setPaymentRequestLoading] = useState(false);

  // ── Corporate session check-in gate ─────────────────────────────────────
  const [checkInBlocked, setCheckInBlocked] = useState(false);

  // ── Phase 1: Room Change (upgrade / downgrade / transfer) ───────────────
  const [roomChangeOpen,    setRoomChangeOpen]    = useState(false);
  const [allRooms,          setAllRooms]          = useState([]);
  const [roomsLoading,      setRoomsLoading]      = useState(false);
  const [selectedNewRoom,   setSelectedNewRoom]   = useState('');
  const [changeReason,      setChangeReason]      = useState('');
  const [changePreview,     setChangePreview]     = useState(null);   // dryRun result
  const [previewLoading,    setPreviewLoading]    = useState(false);
  const [changeConfirming,  setChangeConfirming]  = useState(false);

  const openRoomChange = async () => {
    setRoomChangeOpen(true);
    setSelectedNewRoom('');
    setChangeReason('');
    setChangePreview(null);
    setRoomsLoading(true);
    try {
      const rooms = await fetchRooms(res?.propertyId);
      // Exclude current room and non-available rooms (keep clean & available)
      setAllRooms((rooms || []).filter(rm =>
        rm.id !== res?.roomId &&
        ['clean', 'available', 'dirty'].includes(rm.status?.toLowerCase() || '')));
    } catch { setAllRooms([]); }
    finally { setRoomsLoading(false); }
  };

  const handleRoomSelect = async (newRoomId) => {
    setSelectedNewRoom(newRoomId);
    setChangePreview(null);
    if (!newRoomId) return;
    setPreviewLoading(true);
    try {
      const preview = await previewRoomChange(res.id, newRoomId);
      setChangePreview(preview);
    } catch (e) { setChangePreview({ error: e.message }); }
    finally { setPreviewLoading(false); }
  };

  const handleConfirmRoomChange = async () => {
    if (!selectedNewRoom) return;
    setChangeConfirming(true);
    try {
      await changeRoom(res.id, selectedNewRoom, changeReason);
      setRoomChangeOpen(false);
      load();  // reload reservation data — 'load' is the correct function name here
    } catch (e) { alert('Room change failed: ' + e.message); }
    finally { setChangeConfirming(false); }
  };

  const openInvoice = () => {
    setShowInvoice(true);
    setInvoiceLoading(true);
    fetchFolio(reservationId)
      .then(f => setInvoiceFolio(f))
      .catch(() => setInvoiceFolio(null))
      .finally(() => setInvoiceLoading(false));
  };

  const openGuestRegistration = () => setShowGuestReg(true);

  const openPaymentRequest = () => {
    setShowPaymentRequest(true);
    setPaymentRequestLoading(true);
    fetchFolio(reservationId)
      .then(f => setPaymentRequestFolio(f))
      .catch(() => setPaymentRequestFolio(null))
      .finally(() => setPaymentRequestLoading(false));
  };

  const load = useCallback(() => {
    setLoading(true);
    setLoadErr('');
    fetchReservation(reservationId)
      .then(r => { setRes(r); setLoadErr(''); })
      .catch(e => setLoadErr(e?.message || 'Could not load reservation'))
      .finally(() => setLoading(false));
  }, [reservationId]);

  useOfflineInvalidation('reservations', load);
  useEffect(() => { if (reservationId) load(); }, [reservationId, load]);

  useEffect(() => {
    if (reservationId) warmCacheForView('reservation-detail', reservationId);
  }, [reservationId]);

  useEffect(() => {
    if (!reservationId) return;
    try {
      const KEY = 'hotelmis_visited_reservations';
      const existing = JSON.parse(localStorage.getItem(KEY) || '[]');
      const updated = [reservationId, ...existing.filter(id => id !== reservationId)].slice(0, 60);
      localStorage.setItem(KEY, JSON.stringify(updated));
    } catch (_) {}
  }, [reservationId]);

  // Reset gate block whenever the reservation changes or reloads
  useEffect(() => { setCheckInBlocked(false); }, [reservationId]);

  const showToast = (msg, type='info') => {
    setToast(msg); setToastType(type);
    setTimeout(() => setToast(''), 3800);
  };

  const doAction = async (fn, successMsg) => {
    setBusy(true);
    try {
      const r = await fn();
      if (r?.status && res && (r?.id == null || String(r.id) === String(res.id))) {
        setRes(prev => ({ ...prev, ...r }));
      }
      showToast(r?._offline ? '⏳ Saved offline — will sync when server is back' : successMsg,
        r?._offline ? 'warn' : 'success');
      load();
    } catch (e) { showToast(`${e.message}`, 'error'); }
    finally { setBusy(false); }
  };

  const handleNoShowConfirm = async (feeAmount, reason) => {
    setBusy(true);
    try {
      const r = await markNoShow(res.id, feeAmount, reason);
      setNoShowOpen(false);
      const cur = res.currency || property?.currency || 'RWF';
      showToast(r?._offline
        ? '⏳ No-show saved offline'
        : feeAmount ? `No-show recorded · ${cur} ${Number(feeAmount).toLocaleString()} charged` : 'No-show recorded',
        r?._offline ? 'warn' : 'info');
      load();
    } catch (e) { showToast(e.message, 'error'); }
    finally { setBusy(false); }
  };

  /* ── loading ── */
  if (loading) return (
    <div style={{ display:'flex',flexDirection:'column',alignItems:'center',
      justifyContent:'center',padding:80,gap:16 }}>
      <div style={{ width:40,height:40,border:`3px solid ${T.border}`,borderTopColor:T.teal,
        borderRadius:'50%',animation:'spin 1s linear infinite' }} />
      <p style={{ fontSize:13,color:T.muted }}>Loading reservation…</p>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
  if (!res) return (
    <div style={{ display:'flex',flexDirection:'column',alignItems:'center',
      justifyContent:'center',padding:60,gap:16,textAlign:'center' }}>
      <span style={{ fontSize:40 }}>📋</span>
      <p style={{ fontSize:15,fontWeight:700,color:T.navy }}>Reservation not found</p>
      <p style={{ fontSize:12,color:T.muted,maxWidth:300 }}>
        {loadErr || 'This reservation may not be available offline yet.'}
      </p>
      <div style={{ display:'flex',gap:10,marginTop:8 }}>
        <button onClick={onBack} style={{ padding:'9px 20px',borderRadius:9,background:T.surface,
          border:`1px solid ${T.border}`,fontSize:12,fontWeight:600,cursor:'pointer',color:T.slate,fontFamily:FONTS.body }}>
          ← Back
        </button>
        <button onClick={load} style={{ padding:'9px 20px',borderRadius:9,background:T.teal,
          border:'none',fontSize:12,fontWeight:600,cursor:'pointer',color:'#fff',fontFamily:FONTS.body }}>
          Retry
        </button>
      </div>
    </div>
  );

  const st     = STATUS[res.status] || STATUS.confirmed;
  // Computed from the actual dates rather than trusted from the stored
  // numNights field, which can go stale (e.g. a stay amendment that
  // changes departureDate without recalculating numNights). Falls back to
  // the stored value only if the dates aren't parseable.
  const datesNights = res.arrivalDate && res.departureDate
    ? Math.max(0, Math.round((new Date(res.departureDate) - new Date(res.arrivalDate)) / 86400000))
    : null;
  const nights = datesNights ?? (res.numNights || 0);
  const cur    = res.currency || property?.currency || 'RWF';
  const total  = parseFloat(res.totalAmount || 0);
  const rate   = parseFloat(res.ratePerNight || 0);
  const roomLabel = res.roomNumber
    ? (res.roomTypeName ? `${res.roomTypeName} · Room ${res.roomNumber}` : `Room ${res.roomNumber}`)
    : (res.roomTypeName || 'Not assigned');

  const toastBg = { success:T.success, error:T.danger, warn:'#B45309', info:T.navy };

  return (
    <div className="fade-up">
      <style>{`
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes slideDown{from{opacity:0;transform:translateY(-12px)}to{opacity:1;transform:translateY(0)}}
        .rd-layout{display:grid;grid-template-columns:1fr 300px;gap:16px;align-items:start}
        @media(max-width:900px){.rd-layout{grid-template-columns:1fr}}
      `}</style>

      {/* ── Toast ── */}
      {toast && (
        <div style={{ position:'fixed',top:70,right:24,zIndex:1100,padding:'11px 18px',
          background:toastBg[toastType]||T.navy,color:'#fff',borderRadius:11,
          fontSize:12,fontWeight:600,boxShadow:SHADOW.xl,animation:'slideDown .2s ease',
          maxWidth:320,lineHeight:1.4 }}>{toast}</div>
      )}

      {/* ── Page header ── */}
      <div style={{ display:'flex',alignItems:'flex-start',gap:12,marginBottom:16,flexWrap:'wrap' }}>
        <button onClick={onBack} style={{ display:'flex',alignItems:'center',gap:5,
          background:T.surface,border:`1px solid ${T.border}`,borderRadius:9,
          padding:'7px 14px',fontSize:12,fontWeight:700,color:T.slate,cursor:'pointer',
          fontFamily:FONTS.body,flexShrink:0 }}>
          ← Back
        </button>
        <div style={{ flex:1,minWidth:0 }}>
          <div style={{ display:'flex',alignItems:'center',gap:10,flexWrap:'wrap',marginBottom:3 }}>
            <h1 style={{ fontFamily:FONTS.display,fontSize:22,fontWeight:700,color:T.navy,lineHeight:1 }}>
              {res.confirmationNumber || 'Reservation'}
            </h1>
            <Chip label={st.label} color={st.color} bg={st.bg} />
            {res._offline && <Chip label="Offline" color={T.warning} bg={T.warningPale} />}
          </div>
          <p style={{ fontSize:11,color:T.muted }}>
            {property?.name} · {res.source ? cap(res.source) : 'Direct'}
          </p>
        </div>
      </div>

      {/* ── Hero banner ── */}
      <div style={{ background:`linear-gradient(135deg,${T.navy} 0%,${T.navyLight} 60%,#1F3A52 100%)`,
        borderRadius:16,padding:'20px 24px',marginBottom:16,boxShadow:SHADOW.lg,
        display:'flex',alignItems:'center',gap:20,flexWrap:'wrap' }}>

        <div style={{ width:52,height:52,borderRadius:'50%',background:`${T.teal}50`,
          display:'flex',alignItems:'center',justifyContent:'center',
          fontSize:20,fontWeight:700,color:'#fff',border:`2px solid ${T.teal}60`,flexShrink:0 }}>
          {(res.guestName||'?')[0]?.toUpperCase()}
        </div>

        <div style={{ flex:1,minWidth:160 }}>
          <p style={{ fontSize:17,fontWeight:700,color:'#fff',marginBottom:3 }}>{res.guestName||'—'}</p>
          <p style={{ fontSize:12,color:`rgba(255,255,255,.65)` }}>
            {res.guestEmail}{res.guestPhone ? ` · ${res.guestPhone}` : ''}
          </p>
        </div>

        <div style={{ display:'flex',gap:10,flexWrap:'wrap' }}>
          {[
            { icon:'🛏️', top:'Room',      val:roomLabel },
            { icon:'📅', top:'Check-in',  val:fmtDate(res.arrivalDate) },
            { icon:'📅', top:'Check-out', val:fmtDate(res.departureDate) },
            { icon:'🌙', top:'Nights',    val:`${nights} night${nights!==1?'s':''}` },
          ].map(({icon,top,val}) => (
            <div key={top} style={{ background:'rgba(255,255,255,.08)',borderRadius:10,
              padding:'9px 14px',border:'1px solid rgba(255,255,255,.12)',minWidth:100,textAlign:'center' }}>
              <p style={{ fontSize:9,color:'rgba(255,255,255,.5)',letterSpacing:.8,
                textTransform:'uppercase',marginBottom:3 }}>{top}</p>
              <p style={{ fontSize:12,fontWeight:700,color:'#fff',lineHeight:1.3 }}>{val}</p>
            </div>
          ))}
          <div style={{ background:`rgba(201,168,76,.18)`,borderRadius:10,
            padding:'9px 14px',border:`1px solid ${T.gold}40`,minWidth:100,textAlign:'center' }}>
            <p style={{ fontSize:9,color:`${T.goldLight}99`,letterSpacing:.8,textTransform:'uppercase',marginBottom:3 }}>Total</p>
            <p style={{ fontFamily:FONTS.display,fontSize:18,fontWeight:700,color:T.gold,lineHeight:1 }}>
              {cur} {total.toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      {/* ── Status timeline ── */}
      <StatusBar status={res.status} />

      {/* ── Main layout ── */}
      <div className="rd-layout" style={{ marginTop:16 }}>

        {/* ══ LEFT COLUMN ══ */}
        <div style={{ display:'flex',flexDirection:'column',gap:14 }}>

          {['no_show','cancelled'].includes(res.status) && (
            <div style={{ padding:'14px 18px',borderRadius:12,
              background:st.bg,border:`1px solid ${st.color}30`,display:'flex',gap:12,alignItems:'flex-start' }}>
              <span style={{ fontSize:20,flexShrink:0 }}>{st.icon}</span>
              <div>
                <p style={{ fontSize:13,fontWeight:700,color:st.color }}>{st.label}</p>
                <p style={{ fontSize:12,color:st.color,opacity:.8,marginTop:3,lineHeight:1.5 }}>
                  {res.status==='cancelled'
                    ? 'This reservation has been cancelled.'
                    : 'Guest did not arrive for this reservation.'}
                  {res.cancellationReason && ` — ${res.cancellationReason}`}
                </p>
              </div>
            </div>
          )}

          <SectionCard title="Stay Details" icon="🛏️">
            <InfoRow label="Room"        value={roomLabel} accent={T.teal} />
            <InfoRow label="Check-in"    value={fmtDate(res.arrivalDate)} />
            <InfoRow label="Check-out"   value={fmtDate(res.departureDate)} />
            <InfoRow label="Duration"    value={`${nights} night${nights!==1?'s':''}`} />
            <InfoRow label="Adults"      value={res.adults} />
            <InfoRow label="Children"    value={res.children || '0'} />
            <InfoRow label="Meal Plan"   value={cap(res.mealPlan)} />
            <InfoRow label="Purpose of Visit" value={res.purposeOfVisit || 'Tourism'} />
            {res.reservationCategory === 'corporate' && (
              <InfoRow label="Reservation Type" value={`🏢 Corporate — ${res.corporateAccountName || 'Company'}`} accent={T.teal} />
            )}
            {res.corporateSessionId && (
              <InfoRow label="Session" value={`📁 Corporate Session`} accent={T.teal} />
            )}
            {res.appliedTariffLabel && (
              <InfoRow label="Tariff Applied" value={res.appliedTariffLabel}
                accent={res.appliedTariffType === 'extra_person' ? T.gold : res.appliedTariffType === 'corporate' ? T.teal : undefined} />
            )}
          </SectionCard>

          <SectionCard title="Guest Profile" icon="👤">
            <div style={{ padding:'12px 18px 6px',display:'flex',alignItems:'center',gap:14,
              borderBottom:`1px solid ${T.border}`,marginBottom:2 }}>
              <div style={{ width:44,height:44,borderRadius:'50%',background:`${T.teal}20`,
                display:'flex',alignItems:'center',justifyContent:'center',
                fontSize:17,fontWeight:700,color:T.teal,flexShrink:0 }}>
                {(res.guestName||'?')[0]?.toUpperCase()}
              </div>
              <div>
                <p style={{ fontSize:14,fontWeight:700,color:T.navy }}>{res.guestName}</p>
                {res.guestEmail && <p style={{ fontSize:11,color:T.muted,marginTop:1 }}>{res.guestEmail}</p>}
              </div>
            </div>
            {res.guestPhone       && <InfoRow label="Phone"       value={res.guestPhone} />}
            {res.guestNationality && <InfoRow label="Nationality" value={res.guestNationality} />}
            {res.guestId          && <InfoRow label="Guest ID"    value={res.guestId} accent={T.muted} />}
          </SectionCard>

          <AdditionalGuestsSection reservationId={res.id} focalName={res.guestName} />

          {res.specialRequests && (
            <SectionCard title="Special Requests" icon="💬" accent={T.gold}>
              <div style={{ padding:'12px 18px 14px' }}>
                <p style={{ fontSize:12,color:T.slate,lineHeight:1.7,
                  background:T.goldPale,padding:'10px 14px',borderRadius:9,
                  border:`1px solid ${T.gold}30` }}>{res.specialRequests}</p>
              </div>
            </SectionCard>
          )}

          <SectionCard title="Activity Log" icon="🕐" noPad>
            <div style={{ padding:'8px 18px 14px' }}>
              {[
                { label:'Created',     time:res.createdAt,    icon:'📝', show:true },
                { label:'Last Updated',time:res.updatedAt,    icon:'✏️', show:!!res.updatedAt },
                { label:'Checked In',  time:res.checkedInAt,  icon:'🛎️', show:!!res.checkedInAt },
                { label:'Checked Out', time:res.checkedOutAt, icon:'🚪', show:!!res.checkedOutAt },
              ].filter(e=>e.show).map((e,i,arr) => (
                <div key={e.label} style={{ display:'flex',gap:12,paddingTop:i===0?4:10 }}>
                  <div style={{ display:'flex',flexDirection:'column',alignItems:'center',flexShrink:0 }}>
                    <div style={{ width:28,height:28,borderRadius:'50%',background:`${T.teal}14`,
                      display:'flex',alignItems:'center',justifyContent:'center',fontSize:12 }}>
                      {e.icon}
                    </div>
                    {i < arr.length-1 && <div style={{ width:1,height:10,background:T.border,margin:'3px 0' }} />}
                  </div>
                  <div style={{ paddingBottom:8,borderBottom:i<arr.length-1?`1px solid ${T.border}`:'none',flex:1 }}>
                    <p style={{ fontSize:11,fontWeight:700,color:T.navy }}>{e.label}</p>
                    <p style={{ fontSize:10,color:T.muted,marginTop:1 }}>{fmtDT(e.time)}</p>
                  </div>
                </div>
              ))}
              {res.cancellationReason && (
                <div style={{ marginTop:10,padding:'9px 12px',background:T.dangerPale,
                  borderRadius:8,border:`1px solid ${T.danger}20` }}>
                  <p style={{ fontSize:10,fontWeight:700,color:T.danger,marginBottom:2 }}>Reason</p>
                  <p style={{ fontSize:11,color:T.danger }}>{res.cancellationReason}</p>
                </div>
              )}
            </div>
          </SectionCard>
        </div>

        {/* ══ RIGHT COLUMN — sticky panel ══ */}
        <div style={{ position:'sticky',top:16,display:'flex',flexDirection:'column',gap:14 }}>

          {/* Actions card */}
          <SectionCard title="Actions" icon="⚡">
            <div style={{ padding:'8px 14px 14px',display:'flex',flexDirection:'column',gap:8 }}>

              {res.status === 'tentative' && (
                <Btn variant="primary" icon="✅"
                  onClick={()=>doAction(()=>confirmReservation(res.id),'Reservation confirmed')}
                  disabled={busy}>
                  Confirm Reservation
                </Btn>
              )}

              {/* ── Check-In with corporate session gate ── */}
              {res.status === 'confirmed' && (
                <>
                  {/* Gate alert — renders nothing for non-session reservations */}
                  <CheckInGateAlert
                    reservationId={res.id}
                    onBlock={blocked => setCheckInBlocked(blocked)}
                  />
                  <Btn
                    variant="success"
                    icon="🛬"
                    onClick={() => doAction(() => checkIn(res.id), 'Guest checked in')}
                    disabled={busy || checkInBlocked}
                  >
                    Check In Guest
                  </Btn>
                  {checkInBlocked && (
                    <p style={{ fontSize:10, color:T.warning, textAlign:'center', margin:'-4px 0 0' }}>
                      Payment requirement not met — see above
                    </p>
                  )}
                </>
              )}

              {res.status === 'checked_in' && (
                <Btn variant="navy" icon="🛫"
                  onClick={()=>doAction(()=>checkOut(res.id),'Guest checked out')}
                  disabled={busy}>
                  Check Out Guest
                </Btn>
              )}

              {/* Phase 1: Room Change — available for confirmed & checked-in */}
              {(res.status === 'confirmed' || res.status === 'checked_in') && (
                <Btn variant="default" icon="🔄" onClick={openRoomChange} disabled={busy}>
                  {res.status === 'checked_in' ? 'Move / Upgrade Room' : 'Change Assigned Room'}
                </Btn>
              )}

              {(res.status === 'checked_in' || res.status === 'checked_out') && (
                <Btn variant="gold" icon="🧾" onClick={()=>onNavigate('folio',res.id)} disabled={busy}>
                  View Folio
                </Btn>
              )}

              {(['confirmed','checked_in','checked_out'].includes(res.status)
                || (res.reservationCategory === 'corporate' && res.status === 'tentative')) && (
                <Btn variant="default" icon="🧾" onClick={openInvoice} disabled={busy}>
                  Print Invoice
                </Btn>
              )}

              {['confirmed','checked_in','checked_out'].includes(res.status) && (
                <Btn variant="default" icon="📝" onClick={openGuestRegistration} disabled={busy}>
                  Guest Registration Form
                </Btn>
              )}

              {res.reservationCategory === 'corporate'
                && ['tentative','confirmed','checked_in','checked_out'].includes(res.status) && (
                <Btn variant="default" icon="📨" onClick={openPaymentRequest} disabled={busy}>
                  Payment Request
                </Btn>
              )}

              {['confirmed','tentative'].includes(res.status) && (
                <Btn variant="warning" icon="⛔" onClick={()=>setNoShowOpen(true)} disabled={busy}>
                  Mark No-Show
                </Btn>
              )}

              {!['cancelled','checked_out','no_show'].includes(res.status) && (
                <>
                  {cancelConf ? (
                    <div style={{ background:T.dangerPale,borderRadius:10,padding:'12px 14px',
                      border:`1px solid ${T.danger}30` }}>
                      <p style={{ fontSize:12,fontWeight:700,color:T.danger,marginBottom:8 }}>
                        Cancel this reservation?
                      </p>
                      <div style={{ display:'flex',gap:8 }}>
                        <button onClick={()=>setCancelConf(false)}
                          style={{ flex:1,padding:'8px',borderRadius:8,border:`1px solid ${T.border}`,
                            background:T.white,cursor:'pointer',fontSize:11,fontWeight:600,
                            color:T.muted,fontFamily:FONTS.body }}>Keep</button>
                        <button onClick={()=>{setCancelConf(false);doAction(()=>cancelReservation(res.id),'Reservation cancelled');}}
                          style={{ flex:1,padding:'8px',borderRadius:8,border:'none',
                            background:T.danger,cursor:'pointer',fontSize:11,fontWeight:700,
                            color:'#fff',fontFamily:FONTS.body }}>Yes, Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <Btn variant="danger" icon="✕" onClick={()=>setCancelConf(true)} disabled={busy}>
                      Cancel Reservation
                    </Btn>
                  )}
                </>
              )}

              {!['confirmed','tentative','inquiry','checked_in'].includes(res.status) && (
                <p style={{ textAlign:'center',fontSize:10,color:T.muted,padding:'6px 0' }}>
                  No further actions available
                </p>
              )}
            </div>
          </SectionCard>

          {/* Financial summary */}
          <SectionCard title="Financials" icon="💰">
            <div style={{ padding:'4px 18px 14px' }}>
              <InfoRow label="Rate / Night" value={`${cur} ${rate.toLocaleString()}`} />
              {res.appliedTariffLabel && (
                <InfoRow label="Tariff" value={res.appliedTariffLabel}
                  accent={res.appliedTariffType==='extra_person'?T.gold:res.appliedTariffType==='corporate'?T.teal:T.muted} />
              )}
              <InfoRow label="Nights" value={nights} />
              <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',
                padding:'12px 0',marginTop:4,borderTop:`2px solid ${T.border}` }}>
                <span style={{ fontSize:12,fontWeight:700,color:T.navy }}>Total</span>
                <span style={{ fontFamily:FONTS.display,fontSize:20,fontWeight:700,color:T.gold }}>
                  {cur} {total.toLocaleString()}
                </span>
              </div>
              <div style={{ padding:'9px 12px',background:`${T.teal}08`,borderRadius:9,
                border:`1px solid ${T.teal}20`,marginTop:2 }}>
                <p style={{ fontSize:10,color:T.muted,marginBottom:1 }}>Confirmation #</p>
                <p style={{ fontSize:12,fontWeight:700,color:T.teal,fontFamily:FONTS.mono }}>
                  {res.confirmationNumber || '—'}
                </p>
              </div>
            </div>
          </SectionCard>

          {/* Booking meta */}
          <SectionCard title="Booking Info" icon="📋">
            <InfoRow label="Source"    value={cap(res.source)} />
            <InfoRow label="Meal Plan" value={cap(res.mealPlan)} />
            <InfoRow label="Purpose of Visit" value={res.purposeOfVisit || 'Tourism'} />
            <InfoRow label="Status"    value={st.label} accent={st.color} />
            <InfoRow label="Created"   value={fmtDT(res.createdAt)} />
          </SectionCard>

        </div>{/* end right */}
      </div>

      {/* ── No-Show Modal ── */}
      <NoShowModal open={noShowOpen} res={res} property={property}
        onClose={()=>setNoShowOpen(false)} onConfirm={handleNoShowConfirm} busy={busy} />

      {/* ── Invoice Modal ── */}
      <Modal open={showGuestReg} onClose={()=>setShowGuestReg(false)} title="📝 Guest Registration Form" width={780}>
        <GuestRegistrationPrint reservation={res} property={property} onClose={()=>setShowGuestReg(false)} />
      </Modal>

      <Modal open={showInvoice} onClose={()=>setShowInvoice(false)} title="🧾 Invoice" width={780}>
        {invoiceLoading
          ? <p style={{ fontSize:13,color:T.muted,textAlign:'center',padding:'30px 0' }}>Loading…</p>
          : <InvoicePrint folio={invoiceFolio} reservation={res} property={property} onClose={()=>setShowInvoice(false)} />}
      </Modal>

      {/* ── Phase 1: Room Change Modal ── */}
      <Modal open={roomChangeOpen} onClose={()=>setRoomChangeOpen(false)} title="🔄 Change Room" width={560}>
        <div style={{padding:'4px 0 12px',fontSize:12,color:T.muted}}>
          Current: <strong style={{color:T.navy}}>{roomLabel}</strong>
          {res?.ratePerNight && <span style={{marginLeft:8,color:T.teal}}>@ {res.ratePerNight?.toLocaleString()} / night × {res.numNights} night(s)</span>}
        </div>

        {roomsLoading
          ? <p style={{color:T.muted,textAlign:'center',padding:'20px 0'}}>Loading available rooms…</p>
          : allRooms.length === 0
            ? <p style={{color:T.warning,textAlign:'center',padding:'20px 0'}}>No other rooms available for this property.</p>
            : (
          <div style={{marginBottom:14}}>
            <label style={{fontSize:11,fontWeight:700,color:T.navy,display:'block',marginBottom:6}}>Select New Room *</label>
            <select
              value={selectedNewRoom}
              onChange={e => handleRoomSelect(e.target.value)}
              style={{width:'100%',padding:'9px 12px',borderRadius:8,border:`1px solid ${T.border}`,fontSize:12,fontFamily:'inherit',background:T.surface,color:T.navy}}>
              <option value="">— Choose a room —</option>
              {allRooms.map(rm => (
                <option key={rm.id} value={rm.id}>
                  Room {rm.roomNumber} — {rm.roomTypeName || rm.roomTypeId} [{rm.status}]
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Financial preview */}
        {previewLoading && <p style={{textAlign:'center',color:T.muted,fontSize:12}}>Calculating adjustment…</p>}
        {changePreview && !changePreview.error && (
          <div style={{
            borderRadius:10,padding:'12px 16px',marginBottom:14,
            background: changePreview.changeType === 'upgrade'  ? '#EAF7EE'
                      : changePreview.changeType === 'downgrade' ? '#FEF5E7' : T.tealPale,
            border:`1px solid ${
                      changePreview.changeType === 'upgrade'  ? '#34C76444'
                      : changePreview.changeType === 'downgrade' ? '#E07B0044' : T.teal + '44'}`,
          }}>
            <p style={{fontWeight:800,fontSize:13,color:
              changePreview.changeType === 'upgrade'  ? '#1A7A4A'
              : changePreview.changeType === 'downgrade' ? '#E07B00' : T.teal,
              marginBottom:6,textTransform:'capitalize'}}>
              {changePreview.changeType === 'upgrade'  ? '⬆ Upgrade'
               : changePreview.changeType === 'downgrade' ? '⬇ Downgrade'
               : '↔ Same-Price Transfer'}
            </p>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6,fontSize:11,color:T.muted}}>
              <span>Old rate / night:</span><span style={{fontWeight:700,color:T.navy}}>{Number(changePreview.oldRate).toLocaleString()}</span>
              <span>New rate / night:</span><span style={{fontWeight:700,color:T.navy}}>{Number(changePreview.newRate).toLocaleString()}</span>
              <span>Nights:</span><span style={{fontWeight:700,color:T.navy}}>{changePreview.nights}</span>
              <span>Folio adjustment:</span>
              <span style={{fontWeight:800,fontSize:13,color:
                Number(changePreview.adjustment)>0 ? '#E07B00'
                : Number(changePreview.adjustment)<0 ? '#1A7A4A' : T.muted}}>
                {Number(changePreview.adjustment)===0
                  ? 'None (same price)'
                  : (Number(changePreview.adjustment)>0 ? '+' : '') + Number(changePreview.adjustment).toLocaleString()}
              </span>
            </div>
            {Number(changePreview.adjustment) < 0 && (
              <p style={{fontSize:10,color:'#1A7A4A',marginTop:6}}>A credit of {Math.abs(Number(changePreview.adjustment)).toLocaleString()} will be posted to the folio.</p>
            )}
            {Number(changePreview.adjustment) > 0 && (
              <p style={{fontSize:10,color:'#E07B00',marginTop:6}}>An additional charge of {Number(changePreview.adjustment).toLocaleString()} will be posted to the folio.</p>
            )}
          </div>
        )}
        {changePreview?.error && (
          <div style={{background:'#FEE2E2',borderRadius:8,padding:'10px 14px',marginBottom:12,fontSize:11,color:'#C0392B'}}>
            ⚠ {changePreview.error}
          </div>
        )}

        <div style={{marginBottom:14}}>
          <label style={{fontSize:11,fontWeight:700,color:T.navy,display:'block',marginBottom:6}}>Reason (optional)</label>
          <input value={changeReason} onChange={e=>setChangeReason(e.target.value)}
            placeholder="e.g. Guest requested quieter room, maintenance issue…"
            style={{width:'100%',padding:'9px 12px',borderRadius:8,border:`1px solid ${T.border}`,fontSize:12,fontFamily:'inherit',background:T.surface,color:T.navy,boxSizing:'border-box'}}/>
        </div>

        <div style={{display:'flex',gap:10}}>
          <button onClick={()=>setRoomChangeOpen(false)}
            style={{flex:1,background:'none',border:`1px solid ${T.border}`,borderRadius:9,padding:'10px',cursor:'pointer',fontSize:12,color:T.muted,fontFamily:'inherit'}}>
            Cancel
          </button>
          <button
            disabled={!selectedNewRoom || !changePreview || !!changePreview?.error || changeConfirming}
            onClick={handleConfirmRoomChange}
            style={{flex:2,background:
              changePreview?.changeType==='upgrade' ? '#1A7A4A'
              : changePreview?.changeType==='downgrade' ? '#E07B00' : T.teal,
              border:'none',borderRadius:9,padding:'10px',cursor:'pointer',fontSize:12,fontWeight:700,color:'#fff',fontFamily:'inherit',
              opacity:(!selectedNewRoom||!changePreview||!!changePreview?.error||changeConfirming)?0.5:1}}>
            {changeConfirming ? 'Changing…' :
              changePreview?.changeType === 'upgrade'  ? '⬆ Confirm Upgrade'
              : changePreview?.changeType === 'downgrade' ? '⬇ Confirm Downgrade'
              : '↔ Confirm Room Transfer'}
          </button>
        </div>
      </Modal>

      {/* ── Payment Request Modal — corporate reservations only ── */}
      <Modal open={showPaymentRequest} onClose={()=>setShowPaymentRequest(false)} title="📨 Payment Request" width={740}>
        {paymentRequestLoading
          ? <p style={{ fontSize:13,color:T.muted,textAlign:'center',padding:'30px 0' }}>Loading…</p>
          : <PaymentRequestPrint folio={paymentRequestFolio} reservation={res} property={property} onClose={()=>setShowPaymentRequest(false)} />}
      </Modal>
    </div>
  );
}